import React, { useEffect, useRef, useState } from 'react';
import { COLORS, MAX_COL_WIDTH } from '../constants';

// A thin drag handle on the RIGHT edge of a sticky-left column header. Dragging it
// resizes that column (table-style). The handle straddles the column boundary; the
// last column's handle sits at the day-area edge (resizes the whole left region).
type Props = {
  columnKey: string;
  width: number; // the column's CURRENT width (drag start basis)
  minWidth: number;
  onResize: (key: string, width: number) => void;
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

const ColumnResizer: React.FC<Props> = ({
  columnKey,
  width,
  minWidth,
  onResize,
}) => {
  const [active, setActive] = useState(false);
  const [hover, setHover] = useState(false);
  // Drag bookkeeping (refs so the document listeners read live values).
  const startXRef = useRef(0);
  const startWidthRef = useRef(width);
  const rafRef = useRef(0);
  const pendingRef = useRef<number | null>(null);

  // Tear down any in-flight rAF on unmount.
  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const onMouseDown = (e: React.MouseEvent) => {
    // Don't let the drag bubble into the header (would trigger the column sort).
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    setActive(true);

    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const flush = () => {
      rafRef.current = 0;
      if (pendingRef.current != null) onResize(columnKey, pendingRef.current);
    };

    const onMove = (ev: MouseEvent) => {
      const next = clamp(
        startWidthRef.current + (ev.clientX - startXRef.current),
        minWidth,
        MAX_COL_WIDTH
      );
      pendingRef.current = next;
      // rAF-throttle → one resize per frame (keeps PlateColumn + canvas redraw smooth).
      if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        flush();
      }
      setActive(false);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const lit = active || hover;
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: -3,
        width: 8,
        cursor: 'col-resize',
        zIndex: 5,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {/* Centered 1px divider, highlighted while hovered/dragging. */}
      <span
        style={{
          width: lit ? 2 : 1,
          height: '100%',
          background: lit ? COLORS.todayAccent : COLORS.gridBorder,
        }}
      />
    </span>
  );
};

export default ColumnResizer;
